import React, {useRef} from 'react';
import {AnimatePresence, motion, tokens} from '@sail/motion';
import {view} from '@sail/ui';

import Loading from 'src/components/core/Loading';
import {Header} from 'src/fund/components/screen/Header';
import {ScreenManager} from 'src/fund/components/screen/ScreenManager';
import {useSelector} from 'src/fund/state/store';
import {useFundingSessionContext} from 'src/fund/state/useFundingSessionContext';
import {useStateInitialize} from 'src/fund/state/useStateInitialize';
import {Layout} from 'src/v2/components/screen/Layout';
import {useNotifyParentHeight} from 'src/v2/hooks/useNotifyParentHeight';
import {isEmbedded} from 'src/utils/utils';
import type {RetrieveFundingSessionQueryToken} from 'src/fund/api/useRetrieveFundingSessionQuery';
import type {
  ValidateAllPermissions,
  ComponentToken,
  FunctionParameterToken,
  ValidatePermissions,
} from '@sail/data';

// `token` is a data-v2 query token, not a cryptocurrency token.
// It carries the session's UAT to authenticate retrieval.
const FundingSessionApp = ({
  token,
}: {
  token: RetrieveFundingSessionQueryToken;
}): React.ReactElement => {
  const containerRef = useRef<HTMLDivElement>(null);
  useNotifyParentHeight(isEmbedded() ? containerRef : null);

  const {fundingSessionId} = useFundingSessionContext();
  const {loading} = useStateInitialize(fundingSessionId, token);
  const isSessionLoaded = useSelector(
    (state) => state.cryptoWalletFundingOptions !== null,
  );

  return (
    <view.div ref={containerRef} data-testid="funding-session-app">
      {loading || !isSessionLoaded ? (
        <Loading />
      ) : (
        <AnimatePresence>
          <view.div
            key="loaded"
            uses={[
              motion({
                animate: {opacity: 1},
                initial: {opacity: 0},
                transition: {ease: 'easeIn', duration: tokens.duration.default},
              }),
            ]}
          >
            <Layout header={<Header />}>
              <ScreenManager />
            </Layout>
          </view.div>
        </AnimatePresence>
      )}
    </view.div>
  );
};

export default FundingSessionApp;

// THIS IS AUTOGENERATED CODE FOR PERMISSION TOKEN, DO NOT EDIT AS YOUR CHANGES WILL BE BLOWN AWAY

// prettier-ignore
export type validate = ValidateAllPermissions<
  ValidatePermissions<ComponentToken<typeof FundingSessionApp>, [
    FunctionParameterToken<typeof useStateInitialize, 1>
  ]>
>;
